import { Component, h, Prop, State } from '@stencil/core';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../redux/store';
import {
  addItemToCart,
  canRemoveItemFromCart,
  changeMileageOption,
  fetchItemsForCurrentStep,
  initForOrder,
  markComponentAsLoaded,
  openCheckout,
  removeItemFromCart,
  requireInit,
  resetBooking,
} from '../../redux/actions';
import {
  BookingDetails,
  Cart,
  CartItem,
  OrderKind,
  SplitOption,
  StepItem,
  System,
  UnitConfig,
} from '../../redux/types';
import { RootState } from '../../redux/reducers';
import { RentwareCheckoutTemplate } from '../rentware-checkout/rentware-checkout-modal/template/rentware-checkout-template';
import { Steps } from '../rentware-checkout/rentware-checkout-modal/types';
import { CartStatus, ComponentView, Price, TotalPriceCalculation } from '../../types/types';
import { PickerDate } from '../rtr-article-details/utils/types';
import { t } from '../../redux/utils';
import { Article, NewCartArticle } from './rentware-cart-article/interfaces';
import { makeArticles, makeNewArticles } from './utils';

@Component({
  tag: 'rentware-cart',
  shadow: true,
})
export class RentwareCart {
  @State() waitingArticles: string[] = []; //articles that are waiting for server response; stores item.id
  @State() articlesInCartCorrectOrder: string[] = []; //this array holds articles in order as they were added, prevents from changing their place in the cart; stores item.name
  @State() showVariationModalForId: string;
  @State() showMileageModalForId: string;
  @Prop({ mutable: true }) cartState: Cart;
  @Prop({ mutable: true }) bookingDetails: BookingDetails;
  @Prop({ mutable: true }) system: System;
  @Prop({ mutable: true }) bookingExpired: boolean;
  @Prop({ mutable: true }) stepId: string;
  @Prop({ mutable: true }) currentStepState: StepItem;
  @Prop({ mutable: true }) paymentSplitOption: SplitOption;
  @Prop({ mutable: true }) loadedComponents: { [name: string]: boolean };
  @Prop({ mutable: true }) unitConfig: UnitConfig;
  @Prop() onClose: () => any;
  @Prop() cartView: ComponentView;
  @Prop() checkoutNextClick;
  @Prop() pickerDate: PickerDate; // for details template

  unsubscribe!: Unsubscribe;
  requireInit!: typeof requireInit;
  addItemToCart!: typeof addItemToCart;
  removeItemFromCart!: typeof removeItemFromCart;
  openCheckout!: typeof openCheckout;
  initForOrder!: typeof initForOrder;
  resetBooking!: typeof resetBooking;
  fetchItemsForStep!: typeof fetchItemsForCurrentStep;
  markComponentAsLoaded!: typeof markComponentAsLoaded;
  canRemoveItemFromCart!: typeof canRemoveItemFromCart;
  changeMileageOption!: typeof changeMileageOption;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        cartState: state.cart,
        bookingDetails: state.bookingDetails,
        system: state.system,
        bookingExpired: state.app.bookingExpired,
        stepId: state.app.currentStep?.id,
        loadedComponents: state.app.loadedComponents,
        currentStepState: state.app.currentStep,
        paymentSplitOption: state.app.paymentSplitOption,
        unitConfig: state.system.unitsConfig,
      };
    });

    store.mapDispatchToProps(this, {
      requireInit,
      addItemToCart,
      removeItemFromCart,
      openCheckout,
      initForOrder,
      resetBooking,
      fetchItemsForStep: fetchItemsForCurrentStep,
      markComponentAsLoaded,
      canRemoveItemFromCart,
      changeMileageOption,
    });

    this.markComponentAsLoaded(RentwareCart.name);
  }

  componentDidLoad() {
    this.requireInit();
    this.init();
  }

  componentWillUpdate() {
    this.init();
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  init() {
    // keeping article order
    if (!this.cartState) {
      this.articlesInCartCorrectOrder = [];
    } else {
      // looking for new entries in the list of articles
      this.cartState.items.map(item => {
        if (!this.articlesInCartCorrectOrder.includes(item.name)) {
          this.articlesInCartCorrectOrder.push(item.name);
        }
      });

      // looking if some entry is not removed
      let itemInCarts = [];
      this.cartState.items.map(item => {
        itemInCarts.push(item.name);
      });
      this.articlesInCartCorrectOrder = this.articlesInCartCorrectOrder.filter(name => {
        return itemInCarts.includes(name);
      });
    }
  }

  get cartStatus(): CartStatus {
    if (this.bookingExpired) {
      return CartStatus.Expired;
    } else if (!!this.bookingDetails?.orderDetails) {
      return CartStatus.Active;
    } else {
      return CartStatus.Empty;
    }
  }

  handleRemoveItemFromCart = (id: string, itemId: string) => {
    this.waitingArticles = [...this.waitingArticles, id];

    this.canRemoveItem(
      itemId,
      () => {
        this.removeItemFromCart(
          { itemId: id, amountToRemove: 0 },
          () => {
            this.fetchItemsForStep(() => this.freeArticle(id));
          },
          () => {
            this.fetchItemsForStep(() => this.freeArticle(id));
          },
        );
      },
      () => {
        this.fetchItemsForStep(() => this.freeArticle(id));
      },
    );
  };

  canRemoveItem(itemId: string, onYes = () => null, onNo = () => null) {
    this.canRemoveItemFromCart(
      itemId,
      data => {
        if (!data.success) {
          if (confirm(t('checkout.stepRulesClearCartWarning'))) {
            onYes();
          } else {
            onNo();
          }
        } else {
          onYes();
        }
      },
      undefined,
    );
  }

  handleChangeItemAmount = (id, itemId, newAmount, oldAmount) => {
    if (newAmount >= 0 && newAmount < 1000) {
      const difference = newAmount - oldAmount;
      this.waitingArticles = [...this.waitingArticles, id];

      if (difference >= 1) {
        const article = this.cartState.items.find(item => item.itemId === itemId);
        const isVariation = !!article.products[0]?.variation;
        if (isVariation) {
          this.showVariationModalForId = article.id;
          return;
        }

        this.addItemToCart(
          { itemId: itemId, amountToAdd: difference, people: this.bookingDetails.peopleNumber, name: article.name },
          () => {
            this.fetchItemsForStep(() => this.freeArticle(id));
          },
          () => {
            this.fetchItemsForStep(() => this.freeArticle(id));
          },
        );
      } else if (difference <= 0) {
        this.canRemoveItem(
          itemId,
          () => {
            this.removeItemFromCart(
              { itemId: id, amountToRemove: -difference },
              () => {
                this.fetchItemsForStep(() => this.freeArticle(id));
              },
              () => {
                this.fetchItemsForStep(() => this.freeArticle(id));
              },
            );
          },
          () => {
            this.fetchItemsForStep(() => this.freeArticle(id));
          },
        );
      }
    }
  };

  handleEditItem = (item: CartItem) => {
    if (item.isMileageOption) {
      this.showMileageModalForId = item.itemId;
    }
  };

  handleMileageChange = (mileageItemId: string) => {
    this.showMileageModalForId = mileageItemId;
  };

  freeArticle = (id: string) => {
    this.waitingArticles = this.waitingArticles.filter(articleId => articleId != id);
  };

  handleButton = () => {
    if (this.cartStatus === CartStatus.Active) {
      this.openCheckout();

      const intervalId = setInterval(() => {
        const checkoutLoaded = !!this.loadedComponents[RentwareCheckoutTemplate.name];

        if (!checkoutLoaded) {
          return;
        }

        if (this.onClose) {
          this.onClose();
        }

        clearInterval(intervalId);
      }, 100);
    } else {
      if (this.onClose) {
        this.onClose();
      }
      if (this.cartStatus === CartStatus.Expired) {
        this.resetBooking();
        this.waitingArticles = [];
      }
    }
  };

  handleMileageOptionChange = (itemId: string, optionId: string) => {
    this.waitingArticles = [...this.waitingArticles, itemId];

    this.changeMileageOption(
      itemId,
      optionId,
      () => {
        this.initForOrder(() => {
          this.showMileageModalForId = '';
          this.freeArticle(itemId);
        });
      },
      () => {
        this.showMileageModalForId = '';
        this.freeArticle(itemId);
      },
    );
  };

  handleShowVariationModalForId = (id: string) => {
    if (!this.showVariationModalForId || this.showVariationModalForId !== id) this.showVariationModalForId = id;
    else {
      this.showVariationModalForId = null;
    }
  };

  handleVariationSubmit = (e, id, itemId) => {
    const variations = e.detail as { [id: string]: number };
    const amountToAdd = Object.values(variations).reduce((previousValue: number, currentValue: number) => previousValue + currentValue, 0);
    if (amountToAdd > 0) {
      const variationsIds = [];
      Object.entries(variations).forEach(([id, amount]) => {
        [...Array(amount).keys()].forEach(() => variationsIds.push(id));
      });

      this.addItemToCart({ itemId: itemId, amountToAdd: amountToAdd, variationsIds: variationsIds, people: this.bookingDetails.peopleNumber }, undefined, undefined);
    }

    this.freeArticle(id);
    this.showVariationModalForId = null;
  };

  get buttonIconTotal() {
    return this.cartState?.totalQuantity ?? 0;
  }

  get depositPrice(): Price {
    return this.cartState?.depositPrice;
  }

  get buttonIconPrice(): Price {
    if (this.cartState?.totalPriceCalculation === TotalPriceCalculation.Month) {
      return this.cartState?.discountedPricePerMonthWithoutDeposit;
    }

    return this.cartState?.discountedPriceWithoutDeposit;
  }

  get bookingTitleText() {
    return t('cart.title');
  }

  get backToCartText() {
    return t('cart.backToCart');
  }

  get articlesText() {
    if (this.cartState?.totalQuantityWithShipping === 1) {
      return '1 ' + t('cart.articleInCart');
    }

    return this.cartState?.totalQuantityWithShipping + ' ' + t('cart.articlesInCart');
  }

  get buttonText() {
    if (this.cartStatus === CartStatus.Active) {
      return t('cart.buttonOpenCheckout');
    } else if (this.cartStatus === CartStatus.Empty) {
      return t('cart.buttonEmpty');
    } else if (this.cartStatus === CartStatus.Expired) {
      return t('cart.buttonExpired');
    }
  }

  get articles(): Article[] {
    return makeArticles({
      articlesInCartCorrectOrder: this.articlesInCartCorrectOrder, //
      waitingArticles: this.waitingArticles,
      cart: this.cartState,
      changeAmountCallback: this.handleChangeItemAmount,
      deleteArticleCallback: this.handleRemoveItemFromCart,
      onEdit: this.handleEditItem,
    });
  }

  get newArticles(): NewCartArticle[] {
    return makeNewArticles(this.articles, this.handleMileageChange, this.handleShowVariationModalForId);
  }

  get buttonNextStepText(): string {
    if (this.currentStepState?.kind === Steps.Payment) {
      const key = this.bookingDetails?.orderDetails.kind === OrderKind.Sale ? 'cart.defaultSaleBtn' : 'cart.defaultBtn';
      return t(key);
    } else {
      return t('cart.buttonNextStep');
    }
  }

  get cartShownPrice() {
    if (this.cartState?.totalPriceCalculation === TotalPriceCalculation.Interval) {
      return this.cartState?.discountedPriceWithoutDeposit;
    }

    if (this.cartState?.totalPriceCalculation === TotalPriceCalculation.Month) {
      return this.cartState?.discountedPricePerMonthWithoutDeposit;
    }
  }

  get totalIntervalPrice(): Price {
    return this.cartState?.discountedPriceWithoutDeposit;
  }

  render() {
    // if (this.cartView === ComponentView.Details) {
    //   return (
    //     <rentware-cart-template-details
    //       cartStatus={this.cartStatus}
    //       depositPrice={this.cartState?.depositPrice}
    //       pickerDate={this.pickerDate}
    //       totalPrice={this.cartShownPrice}
    //       onCheckoutButtonClick={this.handleButton}
    //       onClose={this.onClose}
    //       showVariationModalForId={this.showVariationModalForId}
    //       showMileageModalForId={this.showMileageModalForId}
    //       articles={this.newArticles}
    //       shipping={this.cartState?.shipping}
    //       onVariationSubmit={this.handleVariationSubmit}
    //       onMileageOptionChange={this.handleMileageOptionChange}
    //       waitingArticles={this.waitingArticles}
    //       totalQuantity={this.cartState.totalQuantityWithShipping}
    //       distanceUnitAbbreviation={this.system.unitsConfig.distanceUnitAbbreviation}
    //     />
    //   );
    // }

    if (this.cartView === ComponentView.Default || this.cartView === ComponentView.Checkout || this.cartView === ComponentView.Details) {
      return (
        <rentware-cart-template
          bookingId={this.bookingDetails?.orderDetails?.id}
          cartStatus={this.cartStatus}
          showVariationModalForId={this.showVariationModalForId}
          showMileageModalForId={this.showMileageModalForId}
          cartView={this.cartView}
          buttonIconTotal={this.buttonIconTotal}
          buttonIconPrice={this.buttonIconPrice}
          cartPriceCalculation={this.cartState?.totalPriceCalculation}
          headerTitle={this.bookingTitleText}
          buttonText={this.buttonText}
          totalPrice={this.cartShownPrice}
          totalIntervalPrice={this.totalIntervalPrice}
          depositPrice={this.cartState?.depositPrice}
          showDate={this.bookingDetails?.orderDetails?.kind === OrderKind.Rent}
          articlesText={this.articlesText}
          emptyTitleText={t('cart.emptyTitle')}
          emptyHintText={t('cart.emptyHint')}
          expiredTitleText={t('cart.expiredTitle')}
          expiredHintText={t('cart.expiredHint')}
          buttonNextStepText={this.buttonNextStepText}
          backToCartText={this.backToCartText}
          articles={this.articles}
          waitingArticles={this.waitingArticles}
          shipping={this.cartState?.shipping}
          onClose={this.onClose}
          onButton={this.handleButton}
          checkoutNextClick={this.checkoutNextClick}
          onVariationSubmit={this.handleVariationSubmit}
          onMileageOptionChange={this.handleMileageOptionChange}
          onShowVariationModalForId={this.handleShowVariationModalForId}
          splitOption={this.paymentSplitOption}
        />
      );
    }
  }
}
